import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { SaveIcon, RemoveIcon } from '../components/icons';

/**
 * Feature-flag binding for a single tour.
 *
 * Flow:
 *  1. Fetch /v1/admin/feature-flags/providers — tells us which
 *     analytics platform this project forwards to and whether we
 *     have credentials to list its flags.
 *  2. If we do, fetch the flags from that provider and show a
 *     dropdown to pick one (+ the truthy state we care about).
 *  3. Otherwise, fall back to a plain text input the user can type
 *     the flag key into — we don't need the provider API to store
 *     the binding, we need it to make picking easier.
 *
 * Saves are PATCH /v1/admin/tours/{id} with {feature_flag}.  The
 * SDK reads this field; if set and the named flag is falsy on the
 * page, the tour doesn't fire.
 */
export function TourFeatureFlag({ tour, api, onChanged }) {
  const { project } = useOutletContext() || {};

  const [providers, setProviders] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagKey, setFlagKey] = useState(tour?.feature_flag || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // The SDK treats feature_flag as a key that must be truthy on
  // window.posthog or similar.  Anything past "enabled" is up to
  // the provider (variants).  We persist `key` + optional `@value`
  // joined with '@' — the SDK side parses both sides.  `value` is
  // UI-only convenience; left blank → plain truthy check.
  const [, keyPart, , valuePart] = (/^([^@]+)(@(.*))?$/.exec(flagKey) || ['', flagKey, '', '']);
  const [pickedKey, setPickedKey] = useState(keyPart || '');
  const [pickedValue, setPickedValue] = useState(valuePart || '');

  useEffect(() => {
    setFlagKey(tour?.feature_flag || '');
    const m = /^([^@]+)(@(.*))?$/.exec(tour?.feature_flag || '');
    setPickedKey(m?.[1] || '');
    setPickedValue(m?.[3] || '');
  }, [tour?.feature_flag]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await api('/v1/admin/feature-flags/providers');
      setProviders(resp);
      if (resp?.can_fetch_flags && resp?.primary) {
        const flagsResp = await api(
          `/v1/admin/feature-flags?provider=${encodeURIComponent(resp.primary)}`
        ).catch(() => ({ flags: [] }));
        setFlags(flagsResp?.flags || []);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load, project?.project_id]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const newKey = pickedKey.trim();
      const newValue = pickedValue.trim();
      const combined = newKey
        ? (newValue ? `${newKey}@${newValue}` : newKey)
        : '';
      await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...tour, feature_flag: combined }),
      });
      setFlagKey(combined);
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...tour, feature_flag: '' }),
      });
      setPickedKey('');
      setPickedValue('');
      setFlagKey('');
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const selectedFlag = flags.find((f) => f.key === pickedKey);

  return (
    <section className="card" style={{ marginTop: 14 }}>
      <h3 className="card__title">Feature flag</h3>
      <p className="card__body">
        The SDK skips this tour on pages where the named flag isn't
        truthy. Use it to hide tours behind a rollout without touching
        page code — one flag toggle pauses every attached tour at once.
        {flagKey && (
          <> Currently bound to <code>{flagKey}</code>.</>
        )}
      </p>

      {loading && (
        <div className="state" style={{ marginTop: 10 }}>
          <div className="state__title">Loading provider info…</div>
        </div>
      )}

      {!loading && (
        <>
          <ProviderHint providers={providers} />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr auto auto',
              gap: 10,
              alignItems: 'end',
              marginTop: 12,
            }}
          >
            {flags.length > 0 ? (
              <label className="field">
                <span className="field__label">Flag</span>
                <select
                  className="select"
                  value={pickedKey}
                  onChange={(e) => setPickedKey(e.target.value)}
                >
                  <option value="">— none —</option>
                  {flags.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.key}{f.name && f.name !== f.key ? ` · ${f.name}` : ''}
                      {f.active === false ? ' (inactive)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="field">
                <span className="field__label">Flag key</span>
                <input
                  className="input"
                  value={pickedKey}
                  onChange={(e) => setPickedKey(e.target.value)}
                  placeholder="e.g. onboarding_v2"
                />
              </label>
            )}
            <label className="field">
              <span className="field__label">
                Required state (optional)
              </span>
              {selectedFlag?.variants?.length ? (
                <select
                  className="select"
                  value={pickedValue}
                  onChange={(e) => setPickedValue(e.target.value)}
                >
                  <option value="">truthy (any)</option>
                  {selectedFlag.variants.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  value={pickedValue}
                  onChange={(e) => setPickedValue(e.target.value)}
                  placeholder="variant name · or leave blank"
                />
              )}
            </label>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || pickedKey === keyPart && pickedValue === valuePart}
              onClick={save}
            >
              <SaveIcon /> {busy ? 'Saving…' : 'Save'}
            </button>
            {flagKey && (
              <button
                type="button"
                className="btn btn--ghost"
                disabled={busy}
                onClick={clear}
              >
                <RemoveIcon /> Detach
              </button>
            )}
          </div>
          {err && <p style={{ color: 'var(--danger-700)', marginTop: 8 }}>{err}</p>}
        </>
      )}
    </section>
  );
}

function ProviderHint({ providers }) {
  if (!providers?.primary) {
    return (
      <div className="state" style={{ marginTop: 10, textAlign: 'left' }}>
        <div className="state__title">No analytics provider on this project</div>
        <div>
          Flag keys still work — the SDK reads them from
          <code> window.posthog.isFeatureEnabled(...)</code> (or the
          equivalent on your provider) at runtime. To get a live list
          of flags here, turn one on from the <strong>Analytics</strong>
          tab first.
        </div>
      </div>
    );
  }
  if (!providers.can_fetch_flags) {
    return (
      <div className="state" style={{ marginTop: 10, textAlign: 'left' }}>
        <div className="state__title">
          Provider detected: <code>{providers.primary}</code>
        </div>
        <div>
          Type the flag key by hand for now — we'd need a{' '}
          <code>{providers.primary.toUpperCase()}_PERSONAL_API_KEY</code>{' '}
          in the backend to list your flags directly. Once set, the
          dropdown fills automatically.
        </div>
      </div>
    );
  }
  return null;
}
